import { useEffect, useRef, useState } from 'react';
import {
  buildSportsnewPreviewRecoveryClaimKey,
  claimSportsnewPreviewRecoveryKey,
  releaseSportsnewPreviewRecoveryKeyForRetry,
} from '../_helpers/betErrorResync';

const MAX_PROCESSED_PREVIEW_RECOVERIES = 32;
const MAX_PREVIEW_RECOVERY_ATTEMPTS = 2;
const PREVIEW_RECOVERY_RETRY_DELAY_MS = 400;

export default function useSportsnewPreviewRecovery({
  previewKey,
  quoteError,
  selections,
  recoverAfterBetRejection,
  accountKey,
  captureAccountGuard,
  isAccountGuardCurrent,
}) {
  const processedRecoveryKeysRef = useRef(new Set());
  const recoveryAttemptsRef = useRef(new Map());
  const selectionsRef = useRef(selections);
  const quoteErrorRef = useRef(quoteError);
  const recoverAfterBetRejectionRef = useRef(recoverAfterBetRejection);
  const [retryRevision, setRetryRevision] = useState(0);
  const recoveryKey = quoteError?.recoveryKey || '';

  useEffect(() => {
    quoteErrorRef.current = quoteError;
    recoverAfterBetRejectionRef.current = recoverAfterBetRejection;
  }, [quoteError, recoverAfterBetRejection]);

  useEffect(() => {
    selectionsRef.current = selections;
  }, [selections]);

  useEffect(() => {
    const currentQuoteError = quoteErrorRef.current;
    if (!previewKey) {
      processedRecoveryKeysRef.current.clear();
      recoveryAttemptsRef.current.clear();
      return undefined;
    }
    if (!currentQuoteError || currentQuoteError.previewKey !== previewKey) return undefined;

    const accountGuard = captureAccountGuard?.();
    if (
      !accountGuard
      || accountGuard.accountKey !== accountKey
      || !isAccountGuardCurrent?.(accountGuard)
    ) return undefined;

    const scopedRecoveryKey = buildSportsnewPreviewRecoveryClaimKey({
      accountKey,
      recoveryKey,
    });
    if (!claimSportsnewPreviewRecoveryKey({
      processedRecoveryKeys: processedRecoveryKeysRef.current,
      recoveryKey: scopedRecoveryKey,
      limit: MAX_PROCESSED_PREVIEW_RECOVERIES,
    })) return undefined;

    const recoverySelections = Object.freeze(
      selectionsRef.current.map(selection => Object.freeze({ ...selection })),
    );
    let active = true;
    let retryTimer = null;
    const releaseForBoundedRetry = () => {
      if (!active || !scopedRecoveryKey) return;
      const released = releaseSportsnewPreviewRecoveryKeyForRetry({
        processedRecoveryKeys: processedRecoveryKeysRef.current,
        recoveryAttempts: recoveryAttemptsRef.current,
        recoveryKey: scopedRecoveryKey,
        maxAttempts: MAX_PREVIEW_RECOVERY_ATTEMPTS,
        limit: MAX_PROCESSED_PREVIEW_RECOVERIES,
      });
      if (!released.shouldRetry) return;
      retryTimer = setTimeout(() => {
        if (active) setRetryRevision(revision => revision + 1);
      }, PREVIEW_RECOVERY_RETRY_DELAY_MS);
    };

    void recoverAfterBetRejectionRef.current(
      currentQuoteError.source,
      recoverySelections,
      accountGuard,
    )
      .then((result) => {
        if (!active) return;
        const completed = isAccountGuardCurrent?.(accountGuard)
          && (result?.recovered === true || result?.path === 'legacy_force');
        if (completed) {
          recoveryAttemptsRef.current.delete(scopedRecoveryKey);
          return;
        }
        releaseForBoundedRetry();
      })
      .catch(releaseForBoundedRetry);

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    accountKey,
    captureAccountGuard,
    isAccountGuardCurrent,
    previewKey,
    recoveryKey,
    retryRevision,
  ]);
}
